import type { Zone } from "@two-crowns/data";
import { distToPolyline } from "./geometry";

/**
 * Deterministic procedural heightmap shared by client (mesh + entity height) and server
 * (future: line of sight, fall checks). Pure function of (x, z, params).
 */
export interface TerrainParams {
  seed: number;
  amplitude: number;
  scale: number;
  flatRadius: number;
}

function hash(ix: number, iz: number, seed: number): number {
  let h = (ix * 374761393 + iz * 668265263 + seed * 2147483647) | 0;
  h = Math.imul(h ^ (h >>> 13), 1274126177);
  h ^= h >>> 16;
  return (h >>> 0) / 4294967296;
}

function smooth(t: number): number {
  return t * t * (3 - 2 * t);
}

function valueNoise(x: number, z: number, seed: number): number {
  const ix = Math.floor(x);
  const iz = Math.floor(z);
  const fx = smooth(x - ix);
  const fz = smooth(z - iz);
  const a = hash(ix, iz, seed);
  const b = hash(ix + 1, iz, seed);
  const c = hash(ix, iz + 1, seed);
  const d = hash(ix + 1, iz + 1, seed);
  return a + (b - a) * fx + (c - a) * fz + (a - b - c + d) * fx * fz;
}

export function terrainHeight(
  x: number,
  z: number,
  t: TerrainParams,
  center: { x: number; z: number } = { x: 0, z: 0 },
): number {
  let h = 0;
  let amp = 1;
  let freq = 1 / t.scale;
  let norm = 0;
  for (let o = 0; o < 3; o++) {
    h += valueNoise(x * freq, z * freq, t.seed + o * 101) * amp;
    norm += amp;
    amp *= 0.5;
    freq *= 2;
  }
  const d = Math.hypot(x - center.x, z - center.z);
  const flat =
    t.flatRadius > 0 ? smooth(Math.min(1, Math.max(0, (d - t.flatRadius) / t.flatRadius))) : 1;
  return (h / norm) * t.amplitude * flat;
}

/**
 * Local experiment (branch ko-assets-local): height grids of converted ko-assets maps, `n` x `n`
 * corners 4 m apart indexed [x * n + z] in KO's space, which is game space moved by size / 2.
 */
export interface KoHeightField {
  n: number;
  heights: Float32Array;
}
const koFields = new Map<string, KoHeightField>();
export function registerKoHeightField(zoneId: string, field: KoHeightField) {
  koFields.set(zoneId, field);
}

/** CN3Terrain::GetHeight: two triangles per tile, the diagonal alternating like a checkerboard. */
function koHeight(f: KoHeightField, size: number, x: number, z: number): number {
  const kx = Math.min(size - 0.001, Math.max(0, x + size / 2));
  const kz = Math.min(size - 0.001, Math.max(0, z + size / 2));
  const ix = Math.min(f.n - 2, Math.floor(kx / 4));
  const iz = Math.min(f.n - 2, Math.floor(kz / 4));
  const dx = kx / 4 - ix;
  const dz = kz / 4 - iz;
  const h = (a: number, b: number) => f.heights[(ix + a) * f.n + iz + b] ?? 0;
  if ((ix + iz) % 2 === 0) {
    // diagonal from (0,0) to (1,1)
    return dz > dx
      ? h(0, 0) + (h(1, 1) - h(0, 1)) * dx + (h(0, 1) - h(0, 0)) * dz
      : h(0, 0) + (h(1, 0) - h(0, 0)) * dx + (h(1, 1) - h(1, 0)) * dz;
  }
  // diagonal from (1,0) to (0,1)
  return dx + dz > 1
    ? h(1, 1) + (h(1, 1) - h(0, 1)) * (dx - 1) + (h(1, 1) - h(1, 0)) * (dz - 1)
    : h(0, 0) + (h(1, 0) - h(0, 0)) * dx + (h(0, 1) - h(0, 0)) * dz;
}

/**
 * Local experiment: the floors of a ko map (stairs, platforms, bridges): the collision mesh's
 * level triangles, binned on a 4 m grid. A body stands on the highest one it can step onto.
 */
interface KoFloors {
  tris: Float32Array;
  cells: Map<number, number[]>;
}
const koFloors = new Map<string, KoFloors>();
const FLOOR_CELL = 4;
const cellKey = (cx: number, cz: number) => cx * 100003 + cz;
export function registerKoFloors(zoneId: string, tris: Float32Array) {
  const cells = new Map<number, number[]>();
  for (let t = 0; t + 8 < tris.length; t += 9) {
    const xs = [tris[t] ?? 0, tris[t + 3] ?? 0, tris[t + 6] ?? 0];
    const zs = [tris[t + 2] ?? 0, tris[t + 5] ?? 0, tris[t + 8] ?? 0];
    for (
      let cx = Math.floor(Math.min(...xs) / FLOOR_CELL);
      cx <= Math.floor(Math.max(...xs) / FLOOR_CELL);
      cx++
    )
      for (
        let cz = Math.floor(Math.min(...zs) / FLOOR_CELL);
        cz <= Math.floor(Math.max(...zs) / FLOOR_CELL);
        cz++
      ) {
        const k = cellKey(cx, cz);
        const list = cells.get(k) ?? [];
        list.push(t);
        cells.set(k, list);
      }
  }
  koFloors.set(zoneId, { tris, cells });
}

/** How high a body may step up in one move (a stair). */
const STEP_UP_M = 0.6;

/**
 * Local experiment: every collision face of a ko map (CN3ShapeMgr's m_pvCollisions), binned on
 * the same 4 m grid, for ko's own movement check (koMove).
 */
const koFaces = new Map<string, KoFloors>();
export function registerKoCollision(zoneId: string, tris: Float32Array) {
  const cells = new Map<number, number[]>();
  for (let t = 0; t + 8 < tris.length; t += 9) {
    const xs = [tris[t] ?? 0, tris[t + 3] ?? 0, tris[t + 6] ?? 0];
    const zs = [tris[t + 2] ?? 0, tris[t + 5] ?? 0, tris[t + 8] ?? 0];
    for (
      let cx = Math.floor(Math.min(...xs) / FLOOR_CELL);
      cx <= Math.floor(Math.max(...xs) / FLOOR_CELL);
      cx++
    )
      for (
        let cz = Math.floor(Math.min(...zs) / FLOOR_CELL);
        cz <= Math.floor(Math.max(...zs) / FLOOR_CELL);
        cz++
      ) {
        const k = cellKey(cx, cz);
        const list = cells.get(k) ?? [];
        list.push(t);
        cells.set(k, list);
      }
  }
  koFaces.set(zoneId, { tris, cells });
}

/** _IntersectTriangle (Moller-Trumbore): how far along the ray it meets the face, or -1. */
function rayHit(
  t: Float32Array,
  i: number,
  ox: number,
  oy: number,
  oz: number,
  dx: number,
  dz: number,
): number {
  const ax = t[i] ?? 0;
  const ay = t[i + 1] ?? 0;
  const az = t[i + 2] ?? 0;
  const e1x = (t[i + 3] ?? 0) - ax;
  const e1y = (t[i + 4] ?? 0) - ay;
  const e1z = (t[i + 5] ?? 0) - az;
  const e2x = (t[i + 6] ?? 0) - ax;
  const e2y = (t[i + 7] ?? 0) - ay;
  const e2z = (t[i + 8] ?? 0) - az;
  // direction (dx, 0, dz): p = d x e2
  const px = -dz * e2y;
  const py = dz * e2x - dx * e2z;
  const pz = dx * e2y;
  const det = e1x * px + e1y * py + e1z * pz;
  if (Math.abs(det) < 1e-9) return -1;
  const inv = 1 / det;
  const sx = ox - ax;
  const sy = oy - ay;
  const sz = oz - az;
  const u = (sx * px + sy * py + sz * pz) * inv;
  if (u < 0 || u > 1) return -1;
  const qx = sy * e1z - sz * e1y;
  const qy = sz * e1x - sx * e1z;
  const qz = sx * e1y - sy * e1x;
  const v = (dx * qx + dz * qz) * inv;
  if (v < 0 || u + v > 1) return -1;
  return (e2x * qx + e2y * qy + e2z * qz) * inv;
}

/** How far ahead of the feet a step is checked (the body's reach, as ko's 0.5 m lift). */
const KO_BODY_M = 0.4;

/**
 * Local experiment, ko's CPlayerMySelf::CheckCollision for one step: a ray 0.5 m above the feet
 * along the move stops it at the first collision face in the way (a stair's risers are below
 * the ray, walls are not); then the body stands on the nearest face it can step onto, or the
 * terrain (koStandHeight). Returns where the step ends, with its height. `through` (a GM)
 * skips the walls and keeps the height.
 */
/** The longest stretch koMove takes at once (a frame's walk is shorter). */
const KO_SUBSTEP_M = 0.35;

export function koMove(
  zone: Zone,
  from: { x: number; y: number; z: number },
  to: { x: number; z: number },
  through = false,
): { x: number; y: number; z: number } {
  const dx = to.x - from.x;
  const dz = to.z - from.z;
  const dist = Math.hypot(dx, dz);
  if (dist < 1e-6) return { ...from, y: koStandHeight(zone, from.x, from.z, from.y) };
  // a long step (a run skill doubles the stride) goes in short ones, as the client's frames do:
  // each stair is climbed in turn instead of its riser blocking a stride that spans two
  if (dist > KO_SUBSTEP_M) {
    const n = Math.ceil(dist / KO_SUBSTEP_M);
    let at = { ...from };
    for (let i = 1; i <= n; i++) {
      const next = koMove(
        zone,
        at,
        { x: from.x + (dx * i) / n, z: from.z + (dz * i) / n },
        through,
      );
      if (next.x === at.x && next.z === at.z) break;
      at = next;
    }
    return at;
  }
  const f = through ? undefined : koFaces.get(zone.id);
  if (f) {
    const ux = dx / dist;
    const uz = dz / dist;
    const reach = dist + KO_BODY_M;
    const oy = from.y + 0.5;
    const seen = new Set<number>();
    for (let s = 0; s <= reach + FLOOR_CELL; s += FLOOR_CELL / 2) {
      const d = Math.min(s, reach);
      const list = f.cells.get(
        cellKey(
          Math.floor((from.x + ux * d) / FLOOR_CELL),
          Math.floor((from.z + uz * d) / FLOOR_CELL),
        ),
      );
      for (const i of list ?? []) {
        if (seen.has(i)) continue;
        seen.add(i);
        const hit = rayHit(f.tris, i, from.x, oy, from.z, ux, uz);
        if (hit >= 0 && hit <= reach) return { ...from };
      }
      if (d >= reach) break;
    }
  }
  return { x: to.x, z: to.z, y: koStandHeight(zone, to.x, to.z, from.y) };
}

/**
 * Where a body at height `fromY` stands at (x, z), like CN3ShapeMgr::GetHeightNearstPos: of the
 * terrain and the floors it can reach (no higher than fromY + a step), the one nearest its
 * height, so stairs climb and go down, a ledge drops to what is under it, and a floor overhead
 * stays out of reach.
 */
export function koStandHeight(zone: Zone, x: number, z: number, fromY: number): number {
  const ground = zoneHeight(zone, x, z);
  const f = koFloors.get(zone.id);
  if (!f) return ground;
  // the terrain counts only when it can be reached too (it may lie above a cellar floor)
  let best = ground <= fromY + STEP_UP_M ? ground : Number.NEGATIVE_INFINITY;
  const list = f.cells.get(cellKey(Math.floor(x / FLOOR_CELL), Math.floor(z / FLOOR_CELL)));
  if (!list) return ground;
  const t = f.tris;
  for (const i of list) {
    const ax = t[i] ?? 0;
    const az = t[i + 2] ?? 0;
    const bx = t[i + 3] ?? 0;
    const bz = t[i + 5] ?? 0;
    const cx = t[i + 6] ?? 0;
    const cz = t[i + 8] ?? 0;
    const d = (bz - cz) * (ax - cx) + (cx - bx) * (az - cz);
    if (!d) continue;
    const w1 = ((bz - cz) * (x - cx) + (cx - bx) * (z - cz)) / d;
    const w2 = ((cz - az) * (x - cx) + (ax - cx) * (z - cz)) / d;
    const w3 = 1 - w1 - w2;
    if (w1 < -0.001 || w2 < -0.001 || w3 < -0.001) continue;
    const y = w1 * (t[i + 1] ?? 0) + w2 * (t[i + 4] ?? 0) + w3 * (t[i + 7] ?? 0);
    if (y <= fromY + STEP_UP_M && Math.abs(y - fromY) < Math.abs(best - fromY)) best = y;
  }
  return Number.isFinite(best) ? best : ground;
}

/** The ground height anywhere in a zone: hills, border mountains and carved water beds. */
export function zoneHeight(zone: Zone, x: number, z: number): number {
  if (zone.ko) {
    const f = koFields.get(zone.id);
    return f ? koHeight(f, zone.size, x, z) : 0;
  }
  const t = zone.terrain;
  const half = zone.size / 2;
  let h = terrainHeight(x, z, t, zone.spawn);
  // Gentle near town, rugged far out.
  if (t.farBoost !== undefined && t.farBoost !== 1) {
    const far = Math.min(1, Math.hypot(x - zone.spawn.x, z - zone.spawn.z) / half);
    h *= 1 + (t.farBoost - 1) * smooth(far);
  }
  // Mountains along the border (a natural wall instead of an invisible one).
  if (t.edgeStart !== undefined && t.edgeHeight) {
    const m = Math.max(Math.abs(x), Math.abs(z));
    if (m > t.edgeStart) {
      const e = smooth(Math.min(1, (m - t.edgeStart) / Math.max(1, half - t.edgeStart)));
      const ridge = 0.6 + 0.4 * valueNoise(x / 23, z / 23, t.seed + 7);
      h += e * t.edgeHeight * ridge;
    }
  }
  // Nation bases (the PvP colony) sit on level ground.
  const bases = zone.bases;
  if (bases) {
    for (const b of [bases.human, bases.orc]) {
      const d = Math.hypot(x - b.x, z - b.z);
      const r = bases.safeRadius + 8;
      if (d < r * 2) h *= smooth(Math.max(0, (d - r) / r));
    }
  }
  // An arena: a level floor ringed by banks that rise to the rim, then ease back down to the
  // hills outside. Built upward so the floor stays above the zone's water plane.
  const bowl = zone.bowl;
  if (bowl) {
    const d = Math.hypot(x - bowl.x, z - bowl.z);
    const fall = bowl.radius - bowl.floorRadius;
    if (d < bowl.floorRadius) h = 0;
    else if (d < bowl.radius) h = bowl.depth * smooth((d - bowl.floorRadius) / fall);
    else if (d < bowl.radius + fall)
      h = h + (bowl.depth - h) * (1 - smooth((d - bowl.radius) / fall));
  }
  const w = zone.world?.water;
  if (w) {
    const d = waterDistance(zone, x, z);
    if (d < WATER_BANK_M + WATER_VALLEY_M) {
      // Bed, a short bank up to just above the water, then a wide valley back to the hills.
      const bed = w.level - w.depth;
      const shore = w.level + 0.25;
      if (d <= 0) h = bed;
      else if (d < WATER_BANK_M) h = bed + (shore - bed) * smooth(d / WATER_BANK_M);
      else h = shore + (h - shore) * smooth((d - WATER_BANK_M) / WATER_VALLEY_M);
    }
  }
  return h;
}

/** Width of the sloping bank around rivers and lakes. */
export const WATER_BANK_M = 5;
/** The land around water eases down into it over this distance. */
const WATER_VALLEY_M = 32;

/**
 * Signed distance to the nearest water: negative inside a river or lake, positive outside.
 * Used for carving the ground and keeping plants out of the water.
 */
export function waterDistance(zone: Zone, x: number, z: number): number {
  const w = zone.world?.water;
  if (!w) return Number.POSITIVE_INFINITY;
  let best = Number.POSITIVE_INFINITY;
  for (const r of w.rivers) best = Math.min(best, distToPolyline(x, z, r.points) - r.width / 2);
  for (const l of w.lakes) best = Math.min(best, Math.hypot(x - l.x, z - l.z) - l.r);
  return best;
}

/** Bridge decks sit this far above the water surface. */
export const BRIDGE_DECK_ABOVE_WATER = 0.7;
export const BRIDGE_HALF_W = 2.5;
export const BRIDGE_HALF_L = 11;

/** Where characters stand: the ground, or a bridge deck over it. */
export function groundHeight(zone: Zone, x: number, z: number): number {
  const h = zoneHeight(zone, x, z);
  const level = zone.world?.water.level;
  if (level === undefined) return h;
  for (const s of zone.town?.structures ?? []) {
    if (s.type !== "bridge") continue;
    const r = (s.rot * Math.PI) / 180;
    const dx = x - s.x;
    const dz = z - s.z;
    const lx = dx * Math.cos(r) - dz * Math.sin(r);
    const lz = dx * Math.sin(r) + dz * Math.cos(r);
    if (Math.abs(lx) <= BRIDGE_HALF_W && Math.abs(lz) <= BRIDGE_HALF_L)
      return Math.max(h, level + BRIDGE_DECK_ABOVE_WATER);
  }
  return h;
}

/**
 * How far into a region a point is: < 1 inside, > 1 outside. The edge wobbles with noise so
 * forests and meadows get ragged, natural borders instead of circles.
 */
export function regionReach(
  region: { x: number; z: number; r: number },
  x: number,
  z: number,
  seed = 0,
): number {
  const wobble = 0.78 + 0.44 * valueNoise(x / 38 + region.x, z / 38 + region.z, seed + 991);
  return Math.hypot(x - region.x, z - region.z) / (region.r * wobble);
}
